import { SEO } from "@voxmedia/duet-context/context/global-data-context";

export interface GlobalDataSEO {
  defaultSchema: {
    facebookAppId: string | null;
    googleSiteVerification: string | null;
    title: string | null;
  } | null;
  newsMediaOrganizationSchema: {
    description: string | null;
    diversityPolicyUrl: string | null;
    ethicsPolicyUrl: string | null;
    founders: ReadonlyArray<string>;
    foundingDate: string | null;
    knowsAbout: ReadonlyArray<string>;
    linkedInUrl: string | null;
    mastheadUrl: string | null;
    wikipediaUrl: string | null;
  } | null;
}

export function getSiteSettingsSEO(seoData: GlobalDataSEO): SEO {
  return {
    longSiteTitle: seoData.defaultSchema?.title || undefined,
    facebookAppId: seoData.defaultSchema?.facebookAppId || undefined,
    googleSiteVerification:
      seoData.defaultSchema?.googleSiteVerification || undefined,
    longSiteDescription:
      seoData.newsMediaOrganizationSchema?.description || undefined,
    foundingDate:
      seoData.newsMediaOrganizationSchema?.foundingDate || undefined,
    founders: seoData.newsMediaOrganizationSchema?.founders.map((f) => f) || [],
    knowsAbout:
      seoData.newsMediaOrganizationSchema?.knowsAbout.map((f) => f) || [],
    diversityPolicyUrl:
      seoData.newsMediaOrganizationSchema?.diversityPolicyUrl || undefined,
    ethicsPolicyUrl:
      seoData.newsMediaOrganizationSchema?.ethicsPolicyUrl || undefined,
    mastheadUrl: seoData.newsMediaOrganizationSchema?.mastheadUrl || undefined,
    wikipediaUrl:
      seoData.newsMediaOrganizationSchema?.wikipediaUrl || undefined,
    linkedInUrl: seoData.newsMediaOrganizationSchema?.linkedInUrl || undefined,
  };
}

export function generateAlternateName(domain: string): string {
  return domain.replace(
    /(?:www\.)?(.)/,
    (capture) => capture[capture.length - 1]?.toLocaleUpperCase() || "",
  );
}

export function extractTwitterHandle(twitterUrl: string): string | undefined {
  const match = twitterUrl.match(/(?:twitter|x)\.com\/@?([^/]+)\/?$/);
  if (match && match[1]) {
    return "@" + match[1];
  }
  return undefined;
}

/**
 * Returns a value or fallback based on conditions
 * @param value - Primary value to use
 * @param fallbackValue - Fallback value
 * @param useFallback - Whether to use fallback when primary is empty
 * @returns The selected value
 */
export function getValueWithFallback<T>(
  value: T,
  fallbackValue: T,
  useFallback: boolean,
): T {
  return !value && useFallback ? fallbackValue : value;
}

/**
 * Recursively remove undefined, null, empty strings, empty arrays, and
 * (optionally) empty objects from an object.
 *
 * @param obj - The schema object to clean
 * @param removeEmptyObjects - Whether to remove empty objects (default: false)
 */
export function cleanSchema<T>(obj: T, removeEmptyObjects = false): T {
  if (Array.isArray(obj)) {
    // Clean each item in the array and filter out empty arrays/objects if needed
    const cleanedArray = obj
      .map((item) => cleanSchema(item, removeEmptyObjects) as unknown)
      .filter(
        (item) =>
          item !== undefined &&
          item !== null &&
          item !== "" &&
          !(Array.isArray(item) && item.length === 0) &&
          !(
            removeEmptyObjects &&
            typeof item === "object" &&
            !Array.isArray(item) &&
            Object.keys(item as Record<string, unknown>).length === 0
          ),
      );
    return cleanedArray as unknown as T;
  } else if (typeof obj === "object" && obj !== null) {
    const cleanedObj = {} as Record<string, unknown>;
    Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
      const cleanedValue = cleanSchema(value, removeEmptyObjects);
      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        cleanedValue !== "" &&
        !(Array.isArray(cleanedValue) && cleanedValue.length === 0) &&
        !(
          removeEmptyObjects &&
          typeof cleanedValue === "object" &&
          !Array.isArray(cleanedValue) &&
          Object.keys(cleanedValue as Record<string, unknown>).length === 0
        )
      ) {
        cleanedObj[key] = cleanedValue;
      }
    });
    return cleanedObj as unknown as T;
  }
  return obj;
}
